/**
 * Единственная точка подключения стилей: base.css (дословный порт макета) плюс то,
 * чего в макете физически не было.
 *
 * Правило для всех агентов: сначала ищем класс в base.css. Новый класс заводим только
 * если нужного нет, и только в тех же токенах (`var(--…)`), что стоят рядом.
 *
 * Что добавлено ниже и почему:
 *   1. #root                — макет не жил внутри React-контейнера, и .app-container терял ширину;
 *   2. .stage-track         — хлебные крошки стадий (ТЗ §8.5; в макете стадия была одним чипом);
 *   3. .modal-overlay.nested, .modal-sm — вложенные окна подтверждений поверх карточки дела;
 *   4. чат с ИИ-куратором   — поле ввода, вложения в пузыре, отменённый ход, индикатор ожидания;
 *   5. колокольчик          — в макете уведомлений не было вовсе;
 *   6. .cases-row           — таблица списка дел `/cases` (ТЗ §8.4);
 *   7. ярлыки чек-листа и типы ходов куратора в карточке дела;
 *   8. формы (вход, профиль, настройки, ручное обращение);
 *   9. мелочи каркаса;
 *  10. кликабельная строка нормы и панель вкладки карточки (`.tab-pane`);
 *  11. шаг 11: сетка таблиц, подсказка «?», окно дела, анимации (с учётом prefers-reduced-motion).
 */

@import './base.css';

/* ─────────────── 1. Контейнер React ─────────────── */

/* body в макете — flex-контейнер; #root должен растягиваться, иначе .app-container
   схлопывается по содержимому и max-width:1500px перестаёт работать. */
#root {
    width: 100%;
    display: flex;
    justify-content: center;
}

/* ─────────────── 2. Хлебные крошки стадий ─────────────── */

/* Вставляются между .modal-actions и .modal-tabs — на светлой полосе:
   на тёмном фоне шапки цвета .stage-chip не читаются. */
.stage-track {
    display: flex;
    align-items: center;
    gap: 0;
    flex-wrap: wrap;
    list-style: none;
    padding: 10px 22px;
    background: var(--surface-strip);
    border-bottom: 1px solid var(--border);
    flex: none;
}
.stage-track .st-step {
    display: flex;
    align-items: center;
    opacity: 0.45;
}
.stage-track .st-step.done {
    opacity: 0.8;
}
.stage-track .st-step.current {
    opacity: 1;
}
.stage-track .st-step.current .stage-chip {
    box-shadow: 0 0 0 2px var(--navy) inset;
}
.stage-track .st-step + .st-step::before {
    content: '';
    width: 16px;
    height: 2px;
    background: var(--border);
    margin: 0 6px;
    flex: none;
}
.stage-track .st-step.done + .st-step::before {
    background: var(--info);
}

/* ─────────────── 3. Вложенные и узкие модалки ─────────────── */

/* Подтверждение поверх карточки дела: «Отправить заявителю», «Продлить срок». */
.modal-overlay.nested {
    z-index: 60;
    background: rgba(20, 33, 51, 0.35);
}
.modal.modal-sm {
    max-width: 460px;
}

/* ─────────────── 4. Чат с ИИ-куратором ─────────────── */

/* Поле ввода хода под лентой .thread. */
.chat-form {
    margin-top: 16px;
    display: flex;
    flex-direction: column;
    gap: 10px;
}
.chat-input {
    width: 100%;
    min-height: 92px;
    font-size: 14.5px;
    line-height: 1.55;
    padding: 12px 14px;
    border: 1px solid var(--border);
    border-radius: var(--r-xl);
    color: var(--ink);
    background: var(--surface);
    resize: vertical;
}
.chat-input:focus {
    outline: 2px solid var(--focus);
    outline-offset: -1px;
}
.chat-tools {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
}
.chat-note {
    font-size: 14px;
    color: var(--text-muted);
    margin-left: auto;
}
/* Прикреплённый файл до отправки. */
.chat-file {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: var(--text);
    background: var(--surface-soft);
    border: 1px solid var(--border);
    border-radius: var(--r-pill);
    padding: 4px 6px 4px 12px;
    max-width: 100%;
}
.chat-file b {
    color: var(--ink);
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.chat-file button {
    border: none;
    background: none;
    cursor: pointer;
    color: var(--text-muted);
    font-size: 14px;
    line-height: 1;
    padding: 3px 6px;
    border-radius: var(--r-pill);
}
.chat-file button:hover {
    color: var(--late);
    background: var(--late-bg);
}

/* Ход, отменённый через undo: остаётся в ленте, но гаснет. */
.msg.superseded {
    opacity: 0.5;
}
.msg.superseded .msg-undone {
    display: block;
    margin-top: 6px;
    font-size: 13px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    color: var(--text-faint);
}

/* Вложения внутри пузыря сообщения. */
.msg-files {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 10px;
}
.msg-file {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: var(--info);
    background: var(--info-bg);
    border: 1px solid var(--info-border);
    border-radius: var(--r-pill);
    padding: 3px 11px;
    text-decoration: none;
}
.msg-file:hover {
    text-decoration: underline;
}
.msg.user .msg-file {
    color: #fff;
    background: rgba(255, 255, 255, 0.16);
    border-color: rgba(255, 255, 255, 0.24);
}

/* Индикатор ожидания ответа ИИ: пузырь ассистента с бегущими точками. */
.ai-thinking {
    align-self: flex-start;
    display: flex;
    align-items: center;
    gap: 10px;
    max-width: 88%;
    padding: 13px 17px;
    border-radius: var(--r-2xl);
    border-bottom-left-radius: 5px;
    background: var(--surface);
    border: 1px solid var(--border);
    color: var(--text-muted);
    font-size: 14.5px;
}
.ai-dots {
    display: inline-flex;
    gap: 4px;
}
.ai-dots i {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--spark-col);
    animation: ai-dot 1.1s infinite ease-in-out;
}
.ai-dots i:nth-child(2) {
    animation-delay: 0.16s;
}
.ai-dots i:nth-child(3) {
    animation-delay: 0.32s;
}
@keyframes ai-dot {
    0%,
    70%,
    100% {
        opacity: 0.3;
        transform: translateY(0);
    }
    35% {
        opacity: 1;
        transform: translateY(-3px);
    }
}
@media (prefers-reduced-motion: reduce) {
    .ai-dots i {
        animation: none;
        opacity: 0.6;
    }
}

/* ─────────────── 5. Колокольчик уведомлений ─────────────── */

.bell {
    position: relative;
    display: flex;
    align-items: center;
}
.bell-btn {
    position: relative;
    border: none;
    background: rgba(255, 255, 255, 0.15);
    color: #fff;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    font-size: 17px;
    line-height: 1;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
}
.bell-btn:hover {
    background: rgba(255, 255, 255, 0.28);
}
.bell-btn:focus-visible {
    outline: 2px solid var(--focus);
    outline-offset: 2px;
}
.bell-count {
    position: absolute;
    top: -4px;
    right: -4px;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: var(--r-pill);
    background: var(--late);
    color: #fff;
    font-size: 12px;
    font-weight: 700;
    line-height: 18px;
    text-align: center;
}
.bell-pop {
    position: absolute;
    top: calc(100% + 10px);
    right: 0;
    width: 360px;
    max-width: 92vw;
    max-height: 460px;
    display: flex;
    flex-direction: column;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--r-xl);
    box-shadow: var(--sh-modal);
    z-index: 40;
    overflow: hidden;
}
.bell-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 11px 14px;
    background: var(--surface-strip);
    border-bottom: 1px solid var(--border);
}
.bell-title {
    font-size: 14px;
    font-weight: 700;
    color: var(--text-strong);
    text-transform: uppercase;
    letter-spacing: 0.3px;
}
.bell-list {
    overflow: auto;
    flex: 1;
}
.bell-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
    width: 100%;
    text-align: left;
    border: none;
    background: none;
    cursor: pointer;
    padding: 11px 14px;
    border-bottom: 1px solid var(--divider);
    font-size: 14px;
    color: var(--ink);
    line-height: 1.4;
}
.bell-item:hover {
    background: var(--surface-hover);
}
.bell-item:focus-visible {
    outline: 2px solid var(--focus);
    outline-offset: -2px;
}
.bell-item.unread {
    background: var(--info-bg);
}
.bell-item.unread:hover {
    background: var(--surface-hover-blue);
}
.bell-text {
    /* Текст уведомления бывает длинным, а поповер фиксированной ширины. */
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
}
.bell-time {
    font-size: 13px;
    color: var(--text-faint);
}
.bell-empty {
    font-size: 14px;
    color: var(--text-muted);
    padding: 22px 14px;
    text-align: center;
}
.bell-more {
    border: none;
    background: none;
    cursor: pointer;
    width: 100%;
    padding: 10px 14px;
    font-size: 14px;
    font-weight: 600;
    color: var(--info);
    border-top: 1px solid var(--border);
}
.bell-more:hover {
    text-decoration: underline;
}

/* ─────────────── 6. Список дел `/cases` (ТЗ §8.4) ─────────────── */

/* Базовая строка — `.list-row` макета (гибкая, переносится на узком экране);
   на широком превращаем её в сетку с тем же ритмом, что у `.my-row`. */
.cases-row.head {
    font-size: 13px;
    font-weight: 700;
    color: #56697f;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    padding: 7px 10px;
    cursor: default;
    border-bottom: 1px solid #dce3ed;
}
.cases-row.head:hover {
    background: transparent;
}
.cases-row.late-row {
    background: #fef8f8;
}
.cases-row.late-row:hover {
    background: #fdf1f1;
}

@media (min-width: 1151px) {
    .cases-row {
        display: grid;
        grid-template-columns: 128px minmax(0, 1fr) 150px 44px 136px 146px 84px 168px 92px;
        align-items: center;
    }
    .cases-row .my-title,
    .cases-row .my-appl,
    .cases-row .lr-exec {
        min-width: 0;
    }
    .cases-row .lr-exec {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
}

@media (max-width: 1150px) {
    .cases-row.head {
        display: none;
    }
}

/* ─────────────── 7. Карточка дела: ярлык пункта и тип хода ─────────────── */

/* Источник пункта чек-листа (узел 50 мини + `agent` от куратора).
   Базовый .check-src уже синий — он и остаётся ярлыком «поручение». */
.check-src {
    white-space: nowrap;
}
.check-src.src-inquiry {
    background: var(--warn-bg);
    color: var(--warn-text);
}
.check-src.src-applicant,
.check-src.src-clarification {
    background: var(--escalation-bg);
    color: var(--escalation);
}
.check-src.src-agent {
    background: var(--ok-bg);
    color: var(--ok-text);
}
.check-src.src-document {
    background: var(--surface-soft-2);
    color: var(--text-muted);
}

/* Тип хода куратора в .turn-meta. Базовый .turn-badge синий — это «квалификация». */
.turn-badge.tb-question {
    background: var(--info-bg);
    color: var(--info);
}
.turn-badge.tb-facts {
    background: var(--ok-bg);
    color: var(--ok-text);
}
.turn-badge.tb-new {
    background: var(--late-bg);
    color: var(--late);
}

@media (max-width: 780px) {
    .stage-track {
        padding: 8px 14px;
    }
    .stage-track .st-step + .st-step::before {
        width: 8px;
        margin: 0 3px;
    }
    .bell-pop {
        width: 300px;
    }
    /* Ярлык «уточнить у заявителя» длинный: даём тексту пункта уйти на свою строку. */
    .check-item {
        flex-wrap: wrap;
    }
    .check-item .check-text {
        flex: 1 1 100%;
    }
}

/* ─────────────── 8. Формы: вход, профиль, настройки, ручное обращение ─────────────── */

/* Секция восстановлена после того, как параллельная запись файла затёрла её:
   имена классов сохранены, значения взяты из соседних правил base.css. */
.form-grid {
    display: flex;
    flex-direction: column;
    gap: 14px;
}
.form-row {
    display: flex;
    gap: 14px;
    flex-wrap: wrap;
}
.form-row > .form-field {
    flex: 1 1 220px;
}
.form-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
}
.form-field > span {
    font-size: 14px;
    font-weight: 600;
    color: var(--text-muted);
}
.form-input,
.form-select,
.form-textarea {
    width: 100%;
    font-size: 14.5px;
    padding: 10px 13px;
    border: 1px solid var(--border-strong);
    border-radius: var(--r-md);
    color: var(--ink);
    background: var(--surface);
}
.form-textarea {
    min-height: 120px;
    line-height: 1.55;
    resize: vertical;
}
.form-input:focus,
.form-select:focus,
.form-textarea:focus {
    outline: 2px solid var(--focus);
    outline-offset: -1px;
}
.form-input:disabled,
.form-select:disabled,
.form-textarea:disabled {
    background: var(--surface-soft);
    color: var(--text-muted);
}
.form-hint {
    font-size: 14px;
    color: var(--text-muted);
    line-height: 1.5;
}
.form-actions {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    align-items: center;
    margin-top: 4px;
}
.app-container.auth-card {
    max-width: 420px;
}

/* ─────────────── 9. Мелочи ─────────────── */

.link-btn.on-dark {
    color: var(--navy-on-dark);
}
.link-btn.on-dark:hover {
    color: #fff;
}
.user-link {
    border: none;
    background: none;
    cursor: pointer;
    color: inherit;
    font: inherit;
    padding: 0;
}
.user-link:hover {
    text-decoration: underline;
}
.user-link:focus-visible {
    outline: 2px solid var(--focus);
    outline-offset: 2px;
    border-radius: var(--r-xs);
}
.list-empty .state-title {
    font-weight: 600;
    color: var(--ink);
}
.list-empty .state-actions {
    display: flex;
    gap: 10px;
    justify-content: center;
    flex-wrap: wrap;
    margin-top: 12px;
}

/* ─────────────── 10. Кликабельные строки норм и вкладки карточки ─────────────── */

/* `.legal-row` в макете был некликабельным, но ТЗ §8.2 требует переход к делам
   с этой нормой. Оформление повторяет `.cat-row` / `.stat-row` из base.css. */
.legal-row[role='button'] {
    cursor: pointer;
    user-select: none;
}
.legal-row[role='button']:hover .lg-name {
    color: var(--info);
}
.legal-row[role='button']:focus-visible {
    outline: 2px solid var(--focus);
    outline-offset: 2px;
    border-radius: var(--r-xs);
}

/* Панель вкладки карточки дела: скрытие делает атрибут `hidden`, но правило под
   класс должно существовать — иначе `display` на потомке сломает скрытие. */
.tab-pane {
    display: block;
}
.tab-pane[hidden] {
    display: none;
}

/* ─────────────── 11. Удобство и понятность (шаг 11) ─────────────── */

/* Таблица «Дела»: дата поступления — второй строкой под номером дела, освободившееся место — теме обращения
   (на 1280 px тема умещалась в 150 px и переносилась по слову). Заголовок «Приоритет» — полностью. */
@media (min-width: 1151px) {
    .cases-row {
        grid-template-columns: 120px minmax(180px, 1.8fr) minmax(150px, 1.2fr) 84px 132px 124px 140px 84px;
    }
}
.cell-sub {
    display: block;
    margin-top: 2px;
    font-size: 13px;
    color: var(--text-faint);
}
.my-row {
    grid-template-columns: 104px 84px minmax(0, 1fr) 150px 160px 138px 110px;
}
@media (max-width: 1400px) {
    .my-row {
        grid-template-columns: 96px 84px minmax(0, 1fr) 140px 160px 128px 104px;
        gap: 10px;
    }
}

/* Длинная должность в шапке не сдвигает название организации: обрезается многоточием, полностью — по наведению. */
.app-header .user-area .user-link {
    max-width: 260px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* Кнопка-ссылка («Добавить обращение») — как кнопка, без подчёркивания. */
a.act-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    text-decoration: none;
}

/* Дорожка стадий: будущие стадии — серым чипом вместо прозрачности 0,45 (текст терял контраст), пройденные и
   текущая — в полную силу; связки короче, чтобы шесть стадий умещались в одну строку. */
.stage-track .st-step,
.stage-track .st-step.done {
    opacity: 1;
}
.stage-track .st-step:not(.done):not(.current) .stage-chip {
    background: var(--surface-soft);
    color: var(--text-muted);
}
.stage-track .stage-chip {
    padding-left: 8px;
    padding-right: 8px;
}
.stage-track .st-step + .st-step::before {
    width: 10px;
    margin: 0 4px;
}

/* Подсказка «?»: открывается по наведению и с клавиатуры, Esc закрывает. */
.help-tip {
    position: relative;
    display: inline-flex;
    align-items: center;
    vertical-align: middle;
    margin-left: 6px;
}
.help-tip-btn {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    border: 1px solid var(--input-border);
    background: var(--surface);
    color: var(--text-strong);
    font-size: 13px;
    font-weight: 700;
    line-height: 1;
    cursor: help;
    padding: 0;
    flex: none;
}
.help-tip-btn:hover,
.help-tip-btn:focus-visible {
    border-color: var(--focus);
    color: var(--navy);
}
.help-tip-btn:focus-visible {
    outline: 2px solid var(--focus);
    outline-offset: 2px;
}
.help-tip-text {
    position: absolute;
    top: calc(100% + 8px);
    left: -8px;
    z-index: 90;
    width: max-content;
    max-width: min(320px, 80vw);
    padding: 10px 12px;
    border-radius: var(--r-sm);
    background: var(--ink);
    color: #fff;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.45;
    text-transform: none;
    letter-spacing: normal;
    white-space: normal;
    text-align: left;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.22);
    visibility: hidden;
    opacity: 0;
    transform: translateY(-4px);
    transition: opacity 0.15s ease, transform 0.15s ease, visibility 0.15s;
    pointer-events: none;
}
.help-tip-end .help-tip-text {
    left: auto;
    right: -8px;
}
.help-tip:hover .help-tip-text,
.help-tip-btn:focus + .help-tip-text {
    visibility: visible;
    opacity: 1;
    transform: none;
}
.help-tip.help-tip-hidden .help-tip-text {
    visibility: hidden;
    opacity: 0;
}
.act-with-help {
    display: inline-flex;
    align-items: center;
}
.legal-main-head {
    font-size: 14px;
    font-weight: 700;
    color: var(--text-strong);
    margin: 4px 0 6px;
}

/* Окно дела: одна высота на всех вкладках (раньше прыгало между 790 и 970 px на 1920), шире на больших экранах
   (на 1280 дорожка стадий не умещалась в 860 px и переносилась). Прокручивается только тело: шапка, действия и
   вкладки всегда на виду, а при прокрутке шапка сворачивается в одну строку (`.compact`, доработка шага 11). */
.modal.modal-case {
    height: min(90vh, 1000px);
    overflow: hidden;
}
.modal-case .modal-body {
    flex: 1 1 auto;
    min-height: 0;
    overflow: auto;
}
.modal-case .modal-head {
    transition: padding 0.18s ease;
}
.modal-case.compact .modal-head {
    padding-top: 10px;
    padding-bottom: 10px;
    align-items: center;
}
.modal-case.compact .mh-text {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1 1 auto;
    min-width: 0;
}
.modal-case.compact .m-id {
    margin: 0;
    white-space: nowrap;
}
.modal-case.compact .m-prio,
.modal-case.compact .stage-track {
    display: none;
}
.modal-case.compact .modal-head h3 {
    flex: 1 1 auto;
    min-width: 0;
    font-size: 16px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.modal-case.compact .m-chips {
    margin-top: 0;
    flex: none;
    flex-wrap: nowrap;
}
/* В одной строке — номер, тема и первый чип: статус со сроком («В работе · осталось 15 дней», «Просрочено на …»). */
.modal-case.compact .m-chips span:not(:first-child) {
    display: none;
}

/* Нормы правового основания — свёрнутыми строками (доработка шага 11): строка — акт, статья, редакция, оговорка;
   по щелчку раскрывается цитата (тот же `.drawer`, что у раскрывающихся строк светофора: 220 мс, без анимации при
   prefers-reduced-motion). Вместо тире — стрелка раскрытия. */
.legal-norms-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin: 2px 0 4px;
}
.legal-norms-bar .legal-main-head {
    margin: 0;
}
.legal-norm {
    padding: 0;
    border-bottom: 1px solid #f0e2c4;
}
.legal-norm:last-child {
    border-bottom: none;
}
.legal-norm::before {
    content: none;
}
.norm-row {
    display: flex;
    align-items: flex-start;
    gap: 4px;
}
.norm-toggle {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 7px 6px;
    border: none;
    border-radius: var(--r-xs);
    background: transparent;
    color: inherit;
    font: inherit;
    text-align: left;
    cursor: pointer;
}
.norm-toggle:hover {
    background: #f6ecd5;
}
.norm-toggle:focus-visible {
    outline: 2px solid var(--focus);
    outline-offset: 1px;
}
.norm-toggle.norm-static {
    cursor: default;
    padding-left: 28px;
}
.norm-toggle.norm-static:hover {
    background: transparent;
}
.norm-chev {
    flex: none;
    width: 16px;
    color: #7a5a14;
    font-size: 16px;
    line-height: 1.35;
    text-align: center;
    transition: transform 0.18s ease;
}
.norm-open .norm-chev {
    transform: rotate(90deg);
}
.norm-head {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 2px 10px;
    min-width: 0;
}
.norm-act {
    color: #3f3115;
    font-weight: 600;
}
.norm-edition {
    font-size: 13px;
    color: #6b5a33;
}
.norm-head .legal-caveat {
    margin-left: 0;
}
.norm-row .help-tip {
    margin-top: 9px;
}
.norm-quote {
    margin: 0 6px 10px 28px;
}

/* Меню «Ещё» панели действий: редкие действия; панель поверх тела окна. */
.more-menu {
    position: relative;
}
.more-panel {
    position: absolute;
    top: calc(100% + 6px);
    left: 0;
    z-index: 20;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
    min-width: 280px;
    padding: 10px;
    border: 1px solid var(--border);
    border-radius: var(--r-md);
    background: var(--surface);
    box-shadow: 0 10px 28px rgba(20, 33, 51, 0.18);
    animation: gms-rise-in 0.15s ease-out;
}
.more-panel .act-with-help {
    display: flex;
}
.more-panel .act-with-help .act-btn,
.more-panel > .act-btn,
.more-panel .act-select {
    flex: 1 1 auto;
    text-align: left;
}

/* Ошибка запроса с повторами в фоне: пометка рядом с кнопкой «Повторить». */
.retry-note {
    align-self: center;
    font-size: 14px;
    color: var(--text-muted);
}
@media (min-width: 1280px) {
    .modal.modal-case {
        max-width: 920px;
    }
}
@media (min-width: 1440px) {
    .modal.modal-case {
        max-width: 960px;
    }
}

/* Анимации 150–250 мс: окна и подложка, вкладки, всплывающие уведомления, раскрытие колокольчика. */
@keyframes gms-fade-in {
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
}
@keyframes gms-rise-in {
    from {
        opacity: 0;
        transform: translateY(10px) scale(0.985);
    }
    to {
        opacity: 1;
        transform: none;
    }
}
@keyframes gms-toast-in {
    from {
        opacity: 0;
        transform: translate(-50%, 12px);
    }
    to {
        opacity: 1;
        transform: translate(-50%, 0);
    }
}
.modal-overlay.show {
    animation: gms-fade-in 0.18s ease-out;
}
.modal-overlay.show > .modal {
    animation: gms-rise-in 0.22s ease-out;
}
.tab-pane:not([hidden]) {
    animation: gms-fade-in 0.15s ease-out;
}
.toast.show {
    animation: gms-toast-in 0.2s ease-out;
}
.bell-pop {
    animation: gms-rise-in 0.18s ease-out;
}

/* Загрузка: индикатор у строки и скелетон строк таблицы вместо пустоты. */
.loading-line {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
}
.detail-empty.loading-line {
    justify-content: flex-start;
}
.spinner {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    border: 2px solid var(--border);
    border-top-color: var(--info);
    animation: gms-spin 0.8s linear infinite;
    flex: none;
}
@keyframes gms-spin {
    to {
        transform: rotate(360deg);
    }
}
.skeleton-rows {
    padding: 4px 0;
}
.skeleton-row {
    display: grid;
    grid-template-columns: 110px minmax(0, 2fr) minmax(0, 1fr) 120px;
    gap: 16px;
    padding: 16px 10px;
    border-bottom: 1px solid var(--divider);
}
.skeleton-bar {
    display: block;
    height: 12px;
    border-radius: 6px;
    background: linear-gradient(90deg, var(--meter-track) 0%, var(--surface-strip) 50%, var(--meter-track) 100%);
    background-size: 200% 100%;
    animation: gms-shimmer 1.2s ease-in-out infinite;
}
.skeleton-bar.w-sm {
    width: 70%;
}
.skeleton-bar.w-md {
    width: 85%;
}
.skeleton-bar.w-lg {
    width: 95%;
}
@keyframes gms-shimmer {
    from {
        background-position: 100% 0;
    }
    to {
        background-position: -100% 0;
    }
}

@media (prefers-reduced-motion: reduce) {
    *,
    *::before,
    *::after {
        animation-duration: 0.01ms !important;
        animation-iteration-count: 1 !important;
        transition-duration: 0.01ms !important;
        scroll-behavior: auto !important;
    }
}
